/**
 * Admin Entitlements Debug Page
 * =============================
 * Full-page version of the entitlements debug panel for detailed analysis.
 * Only accessible to admins in non-production environments.
 */

import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../App";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  RefreshCw,
  User,
  Shield,
  Gauge,
  Settings,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  Download
} from "lucide-react";

const API = window.location.origin;

// Check if debug panel should be enabled
const IS_DEV = process.env.NODE_ENV !== 'production';
const DEBUG_PANELS_ENABLED = process.env.REACT_APP_ENABLE_DEBUG_PANELS === 'true';
const PANEL_ALLOWED = IS_DEV || DEBUG_PANELS_ENABLED;

function BoolIndicator({ value, label }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50 border border-slate-700">
      <span className="text-sm text-slate-300">{label}</span>
      {value ? (
        <div className="flex items-center gap-1 text-emerald-400">
          <CheckCircle2 className="w-4 h-4" />
          <span className="text-xs font-medium">TRUE</span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-slate-500">
          <XCircle className="w-4 h-4" />
          <span className="text-xs font-medium">FALSE</span>
        </div>
      )}
    </div>
  );
}

function NumericValue({ label, value, unit = "" }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50 border border-slate-700">
      <span className="text-sm text-slate-300">{label}</span>
      <span className="text-sm font-mono text-amber-400">
        {value ?? "—"}{unit && ` ${unit}`}
      </span>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
        <Icon className="w-5 h-5 text-slate-400" />
        <span className="font-medium text-slate-200">{title}</span>
      </div>
      <div className="p-4 space-y-2">
        {children}
      </div>
    </div>
  );
}

function TierBadge({ tier, isActive }) {
  const tierColors = {
    free: "bg-slate-600 text-slate-200",
    inner_circle: "bg-purple-600 text-purple-100",
    elite: "bg-amber-600 text-amber-100",
    elite_plus: "bg-gradient-to-r from-amber-500 to-rose-500 text-white",
    couple_bundle: "bg-pink-600 text-pink-100",
    admin: "bg-red-600 text-red-100",
  };
  
  return (
    <div className="flex items-center gap-3">
      <span className={`text-sm px-3 py-1 rounded-full font-medium ${tierColors[tier] || tierColors.free}`}>
        {tier?.replace(/_/g, ' ').toUpperCase()}
      </span>
      {isActive !== undefined && (
        <span className={`flex items-center gap-1 text-sm ${isActive ? 'text-emerald-400' : 'text-rose-400'}`}>
          <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-rose-400'}`} />
          {isActive ? 'Active' : 'Inactive'}
        </span>
      )}
    </div>
  );
}

export default function AdminEntitlementsDebugPage() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Redirect if not allowed
  useEffect(() => {
    if (!PANEL_ALLOWED || !user?.is_admin) {
      navigate('/admin');
    }
  }, [user, navigate]);
  
  const fetchEntitlements = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/me/entitlements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      setEntitlements(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);
  
  const copyToClipboard = () => {
    if (entitlements) {
      navigator.clipboard.writeText(JSON.stringify(entitlements, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const downloadJson = () => {
    if (entitlements) {
      const blob = new Blob([JSON.stringify(entitlements, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `entitlements_${user?.id?.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };
  
  if (!PANEL_ALLOWED || !user?.is_admin) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-entitlements-debug-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold">Entitlements Debug</h1>
              <p className="text-sm text-slate-400">Read-only view of current user&apos;s entitlements</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={fetchEntitlements}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh</span>
            </button>
            <button
              onClick={copyToClipboard}
              disabled={!entitlements}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Copy className="w-4 h-4" />
              <span className="text-sm">{copied ? 'Copied!' : 'Copy JSON'}</span>
            </button>
            <button
              onClick={downloadJson}
              disabled={!entitlements}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Download</span>
            </button>
          </div>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <span className="text-rose-400">Error: {error}</span>
          </div>
        )}
        
        {loading && !entitlements && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
          </div>
        )}
        
        {entitlements && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* User Info */}
              <Section icon={User} title="User Info">
                <NumericValue label="User ID" value={user?.id} />
                <NumericValue label="Email" value={user?.email} />
                <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <span className="text-sm text-slate-300">Effective Tier</span>
                  <TierBadge 
                    tier={entitlements.effective_tier || entitlements.subscription?.tier} 
                    isActive={entitlements.subscription?.is_active}
                  />
                </div>
                <BoolIndicator label="Is Premium" value={entitlements.is_premium} />
                <BoolIndicator label="Is Admin" value={user?.is_admin} />
              </Section>
              
              {/* Subscription */}
              <Section icon={Shield} title="Subscription">
                <NumericValue label="Tier" value={entitlements.subscription?.tier} />
                <NumericValue label="Source" value={entitlements.subscription?.source || "none"} />
                <BoolIndicator label="Active" value={entitlements.subscription?.is_active} />
                <BoolIndicator label="In Grace Period" value={entitlements.subscription?.in_grace_period} />
                <BoolIndicator label="Scheduled to Cancel" value={entitlements.subscription?.scheduled_to_cancel} />
                {entitlements.subscription?.expires_at && (
                  <NumericValue label="Expires At" value={new Date(entitlements.subscription.expires_at).toLocaleString()} />
                )}
              </Section>
              
              {/* Verification */}
              <Section icon={Shield} title="Verification Status">
                <BoolIndicator label="Identity Verified" value={entitlements.verification?.identity_verified} />
                <BoolIndicator label="Age Verified" value={entitlements.verification?.age_verified} />
                <BoolIndicator label="Email Verified" value={entitlements.verification?.email_verified} />
              </Section>
              
              {/* Limits */}
              <Section icon={Gauge} title="Limits">
                {entitlements.limits && Object.entries(entitlements.limits)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <NumericValue key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
                {entitlements.daily_lounge_minutes !== undefined && (
                  <NumericValue label="Daily Lounge Minutes (Legacy)" value={entitlements.daily_lounge_minutes} unit="min" />
                )}
                {entitlements.lounge_usage_seconds_today !== undefined && (
                  <NumericValue label="Lounge Used Today" value={Math.round(entitlements.lounge_usage_seconds_today / 60)} unit="min" />
                )}
              </Section>
            </div>
            
            {/* Right Column */}
            <div className="space-y-6">
              {/* Features */}
              <Section icon={Settings} title="Features (Boolean Flags)">
                <div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto pr-2">
                  {entitlements.features && Object.entries(entitlements.features)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => (
                      <BoolIndicator key={key} label={key.replace(/_/g, ' ')} value={value} />
                    ))
                  }
                </div>
              </Section>
              
              {/* Thresholds */}
              <Section icon={Settings} title="Thresholds">
                {entitlements.thresholds && Object.entries(entitlements.thresholds)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <NumericValue key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
              </Section>
              
              {/* Requirements */}
              <Section icon={AlertTriangle} title="Requirements">
                {entitlements.requirements && Object.entries(entitlements.requirements)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <BoolIndicator key={key} label={key.replace(/_/g, ' ')} value={value} />
                  ))
                }
              </Section>
              
              {/* Raw JSON Preview */}
              <Section icon={Settings} title="Raw Response">
                <div className="max-h-[300px] overflow-auto">
                  <pre className="text-xs text-slate-400 font-mono whitespace-pre-wrap break-words">
                    {JSON.stringify(entitlements, null, 2)}
                  </pre>
                </div>
              </Section>
            </div>
          </div>
        )}
        
        {/* Footer Metadata */}
        {entitlements && (
          <div className="mt-8 pt-4 border-t border-slate-800 flex items-center justify-between text-sm text-slate-500">
            <span>API Version: {entitlements.version}</span>
            <span>Server Time: {new Date(entitlements.server_timestamp).toLocaleString()}</span>
          </div>
        )}
      </main>
    </div>
  );
}
